import zipWith from 'lodash/zipWith';
import { BUNDLE_REGEXP, HIGHLIGHT_DIFFERENCES_EXCEPTION_LIST } from '../config/consts';
import { PLACEHOLDERS, URLS } from '@pes-ui/utils/lib/constants';
import { ProductsParametersApi } from '@pes-ui/utils/lib/types/api/products-parameters-api';
import { MappedProduct } from './mappers';
import { formatString, getLocaleFromBody } from '@pes-ui/utils';
import type { ComparatorNavigation, OutsideNavigation } from '../types';


export const removeZeroValues = objWithValues =>
    Object.keys(objWithValues).reduce((acc, key) => {
        if (objWithValues[key] === 0) {
            return acc;
        }

        return { ...acc, [key]: objWithValues[key] };
    }, {});

export const normalizePreferencesFromParams = ({ powerMargin, runtime }) => {
    try {
        return {
            powerMargin: typeof powerMargin === 'undefined' ? 0 : Math.round(+powerMargin * 100),
            runtime: typeof runtime === 'undefined' ? 0 : +runtime,
        };
    } catch (error) {
        return {
            runtime: 0,
            powerMargin: 0,
        };
    }
};

const convertToArray = value => (Array.isArray(value) ? value : [value]);

/**
 * Brings 'property' and 'key' values into the same shape (into Array of objects)
 *
 * @param {object} property - productCategory values
 * @param {string} key - category name
 * @returns {Array}
 */
export const normalizeProductValue = (
    property: Record<ProductsParametersApi.ParameterTypes, ProductsParametersApi.ItemParameter>,
    key
) => {
    if (!(property && property[key])) {
        return [];
    }

    // Enable flag for 'rbkInterfaces' and 'preinstalledCards' properties (as they have additional 'IconExternalLink' icon)
    const hasExternalLink = key === 'rbkInterfaces' || key === 'preinstalledCards';
    const { attributes, url, value, runTimeError = '', isRuntimeError = '' } = property[key];
    const urlList = convertToArray(url);
    const valueList = convertToArray(value);
    const runTimeErrorList = convertToArray(runTimeError);
    const isRunTimeErrorList = convertToArray(isRuntimeError);
    const attributeList = attributes && convertToArray(attributes.isDisplayLinkIcon);
    const openPopupList = attributes && convertToArray(attributes.openPopup);

    // Return new array of grouped properties
    return zipWith(
        urlList,
        valueList,
        runTimeErrorList,
        isRunTimeErrorList,
        attributeList,
        openPopupList,
        (productUrl, productValue, productRunTimeError, isProductRuntimeError, chartLabel) => ({
            productUrl,
            productValue,
            productRunTimeError,
            hasExternalLink,
            isProductRuntimeError,
            chartLabel,
            openPopupList
        }),
    );
};

/**
 * Receives 'products' (Array) and 'categoryValue' (String)
 * Returns Array of 'products' that contains 'categoryValue'
 *
 * @param {array} products
 * @param {string} categoryValue
 * @returns {Array}
 */
export const getProductsWithCategory = (products: MappedProduct[], categoryValue) =>
    products.filter(product => product.productCategories[categoryValue]);

/**
 * Receives 'category' (Object) and 'products' (Array)
 * Maps through each 'category' key, and checks if there is any product that contains that 'key'
 *
 * @param {object} category
 * @param {array} products
 * @returns {boolean}
 */
export const isCategorySectionAvailable = (category, products) => {
    const categoryValues = Object.keys(category.parameters);
    const categoryProperties = categoryValues.map(value =>
        getProductsWithCategory(products, value),
    );
    const availableCategoryProperties = categoryProperties.filter(property => property.length);

    return Boolean(availableCategoryProperties.length);
};

/**
 * Receives 'value' (String) and 'isRowHighlighted' (Boolean)
 * Checks if 'HIGHLIGHT_DIFFERENCES_EXCEPTION_LIST' includes 'value'
 * Returns 'true' / 'false'
 *
 * Responsible for highlighting 'category row' on Comparison Page
 *
 * @param value
 * @param isRowHighlighted
 * @returns {boolean}
 */
export const isCategoryRowHighlighted = (value, isRowHighlighted) =>
    !(HIGHLIGHT_DIFFERENCES_EXCEPTION_LIST.includes(value) || !isRowHighlighted);

/**
 * Receives bundle string
 * Parses to object with properties
 * Example XB4BV5B5:(4)XB4BV5B321 =>
 * {
 *    productId: XB4BV5B5,
 *    bundledProductId: XB4BV5B321,
 *    quantity: 4,
 * }
 * @param bundle
 */
export const parseBundleFromString = (bundle: string) => {
    if(!bundle.match(BUNDLE_REGEXP)){
        return null;
    }
    const productId = bundle.split(':')[0];
    const quantity = bundle.match(/\((.*)\)/)?.pop();
    const bundledProductId = bundle.split(')')[1];
    return {
        productId,
        bundledProductId,
        quantity
    }
}

// For apc 
export const goToCartUrl = (hostUrl: string, countryCode: string, languageCode: string,  apcProductId: string, apcBundleId?: string, qty?: string, agentChatCscId?: string ) => {
    return formatString(URLS.COMPARISON_ADD_TO_CART, {
        [PLACEHOLDERS.HOST]: hostUrl,
        [PLACEHOLDERS.COUNTRY]: countryCode.toLowerCase(),
        [PLACEHOLDERS.LANGUAGE]: languageCode,
        [PLACEHOLDERS.SKUS]: apcBundleId ? `${apcProductId},${apcBundleId}`: apcProductId,
        [PLACEHOLDERS.QTY]: apcBundleId ? `${1},${qty}`: '1',
        ...(agentChatCscId && {[PLACEHOLDERS.Chat_CSC_ID]: agentChatCscId})
    });
};

// China is a special case -- its "country code" comes from the hostname
export const isHostnameContainsCC = (): boolean => {
    return window.location.hostname.toLowerCase().endsWith('cn');
}

export function getComparatorPageURL(params: ComparatorNavigation) {
    if (isHostnameContainsCC()) return `/${params.languageCode}/${params.pageRoute}`;
    return `/${params.countryCode}/${params.languageCode}/${params.pageRoute}`;
}

export function getOutsideURL(params: OutsideNavigation) {
    let baseURL = '';
    if (!params.isPublic) baseURL = params.baseURL;
    if (isHostnameContainsCC()) return `${baseURL}/${params.languageCode}/${params.pageRoute}`;
    return `${baseURL}/${params.countryCode}/${params.languageCode}/${params.pageRoute}`;
}

export const covertImageUrlToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const response = reader.result;
            resolve(response); 
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export const getObjectKeys = (obj) => {
    return obj ? Object.keys(obj) : [];
}

export const consoleErrorMsg = (err, origin: string) => {
    console.error(`Error in ${origin}`, err);
}

export const getLocaleInfo = (countryCode: string, languageCode: string): string  => {
  if (!isHostnameContainsCC()) return `/${countryCode}/${languageCode}`;

  const getLocaleCode = getLocaleFromBody();
  return `/${getLocaleCode?.countryCode?.toUpperCase()}/${getLocaleCode?.languageCode}`;
}
